import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Plus, Trash2, Edit3, Star, Video, Image as ImageIcon } from 'lucide-react';
import ImageUploadField from './ImageUploadField';

const TestimonialsPanel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [current, setCurrent] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const data = await api.getTestimonials();
      setTestimonials(data);
    } catch (err) {
      console.error('Failed to load:', err);
    }
  };

  const handleNew = () => {
    setCurrent({ name: '', text: '', role: '', image_url: '', video_url: '', order: testimonials.length });
    setIsEditing(true);
  };

  const handleEdit = (item) => {
    setCurrent({ ...item });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      if (current.id) {
        await api.updateTestimonial(current.id, current);
      } else {
        await api.createTestimonial(current);
      }
      setMessage('Testimonial saved!');
      setIsEditing(false);
      load();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await api.deleteTestimonial(id);
      load();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-6" data-testid="testimonial-editor">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{current.id ? 'Edit' : 'New'} Testimonial</h2>
          <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        </div>

        {message && <div className={`p-4 rounded ${message.includes('saved') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>{message}</div>}

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Client Name *</label>
              <input type="text" value={current.name || ''} onChange={(e) => setCurrent({ ...current, name: e.target.value })} className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" placeholder="e.g., Sarah & James" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role / Wedding Type</label>
              <input type="text" value={current.role || ''} onChange={(e) => setCurrent({ ...current, role: e.target.value })} className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" placeholder="e.g., Pakistani Wedding, 2024" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Review Text *</label>
            <textarea value={current.text || ''} onChange={(e) => setCurrent({ ...current, text: e.target.value })} rows={5} className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" placeholder="What the client said about their experience..." />
          </div>

          <ImageUploadField
            value={current.image_url || ''}
            onChange={(url) => setCurrent({ ...current, image_url: url })}
            folder="testimonials"
            label="Client Photo (optional)"
          />

          <div>
            <label className="block text-sm font-medium mb-1">Video URL (optional)</label>
            <input type="url" value={current.video_url || ''} onChange={(e) => setCurrent({ ...current, video_url: e.target.value })} className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" placeholder="https://youtube.com/watch?v=..." />
            <p className="text-xs text-gray-400 mt-1">YouTube or Vimeo link to their wedding video</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Display Order</label>
            <input type="number" value={current.order || 0} onChange={(e) => setCurrent({ ...current, order: parseInt(e.target.value) || 0 })} className="w-24 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>

          <button onClick={handleSave} disabled={!current.name || !current.text} className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-40" data-testid="save-testimonial-btn">
            Save Testimonial
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="testimonials-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Testimonials</h2>
          <p className="text-sm text-gray-500 mt-1">Manage client reviews displayed on the About page</p>
        </div>
        <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors" data-testid="add-testimonial-btn">
          <Plus size={14} /> Add Testimonial
        </button>
      </div>

      {message && <div className="p-3 bg-green-50 text-green-800 rounded text-sm">{message}</div>}

      <div className="space-y-3">
        {testimonials.map((item) => (
          <div key={item.id} className="flex items-start gap-4 p-4 border rounded-lg hover:border-gray-300 transition-colors" data-testid={`testimonial-${item.id}`}>
            {item.image_url ? (
              <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Star size={20} className="text-gray-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">{item.name}</p>
                {item.role && <span className="text-xs text-gray-400">/ {item.role}</span>}
                {item.video_url && <Video size={14} className="text-blue-400" />}
              </div>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">"{item.text}"</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => handleEdit(item)} className="p-2 text-gray-400 hover:text-gray-600"><Edit3 size={14} /></button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <Star size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No testimonials yet</p>
          <button onClick={handleNew} className="mt-3 text-sm text-blue-600 hover:text-blue-800">Add your first review</button>
        </div>
      )}
    </div>
  );
};

export default TestimonialsPanel;
